import React, { useState, useRef, useCallback } from 'react';
import { Container, Card, Modal, Button } from 'react-bootstrap';
import { RefreshCw, X } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGlowpick, type NavigationTab } from '../../../apps/hooks/sourcing/useGlowpick';
import { useCoupang } from '../../../apps/hooks/sourcing/useCoupang';
import { skinSeoulProductsClient } from '../../../infrastructure/SkinSeoulProductsClient';
import type { GlowpickProduct } from '../../../models/Glowpick';
import type { SkinSeoulAdminProduct } from '../../../models/ProductTypes';
import { cpGpMatchClient } from '../../../infrastructure/CpGpMatchClient';
import { Toast } from './components/Toast';
import { IngredientsModal } from './components/GlowpickIngredientsModal';
import { DescriptionModal } from './components/GlowpickDescriptionModal';
import { GlowpickProductsTable } from './components/GlowpickProductsTable';
import { SkinSeoulProductsTable } from './components/SkinSeoulProductsTable';
import { CoupangProductsTable } from './components/CoupangProductsTable';
import { convertToLocalDate } from '../../../utils/dateUtils';
import { ROUTES } from '../../../routes/routes';
import { IdBadge, IdBadgeType } from '../../../components/ui/badge/IdBadge';

const SourcingPage = () => {
  // Get URL parameters
  const { glowpickId, brand, category, subcategory } = useParams<{
    glowpickId?: string;
    brand?: string;
    category?: string;
    subcategory?: string;
  }>();

  // Use the updated hook with new navigation structure
  const glowpickHook = useGlowpick();
  const {
    // Products and data
    glowpickProducts,
    categories,
    subcategories,
    awardCategories,
    brands,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,

    // Navigation state
    activeTab,
    selectedCategory,
    selectedSubcategory,
    selectedBrand,
    selectedRankingType,
    selectedAwardPeriod,
    sortBy,
    queryLimit,

    // Loading states
    loading,

    // Last updated info
    lastUpdatedInfo,

    // Actions
    handleTabChange,
    handleCategoryChange,
    setSelectedSubcategory,
    setSelectedBrand,
    setSelectedRankingType,
    setSelectedAwardPeriod,
    setSortBy,
    setQueryLimit,
    getProductById,
    fetchProductDetails,
    silentRefresh,
    clearProductMetadata,
    setSearchProduct,
  } = glowpickHook;

  // Use Coupang hook
  const {
    coupangProducts,
    loading: coupangLoading,
    searchCoupangProducts,
    setSelectedCoupangProduct,
    triggerScrapeBatchJob,
    clearCoupangProducts,
    deleteKeywordSearchResults,
  } = useCoupang();

  // Local state for UI interactions
  const [selectedGlowpickProduct, setSelectedGlowpickProduct] = useState<GlowpickProduct | null>(null);

  // SkinSeoul products state
  const [skinSeoulProducts, setSkinSeoulProducts] = useState<SkinSeoulAdminProduct[]>([]);
  const [skinSeoulLoading, setSkinSeoulLoading] = useState(false);

  // SKU search state
  const [skuSearchValue, setSkuSearchValue] = useState('');
  const [skuSearchLoading, setSkuSearchLoading] = useState(false);

  // Resizable layout state for 3 panels
  const [glowpickWidth, setGlowpickWidth] = useState(30); // 30% for Glowpick
  const [skinSeoulWidth, setSkinSeoulWidth] = useState(35); // 35% for SkinSeoul
  // Coupang width is calculated as: 100 - glowpickWidth - skinSeoulWidth
  const [isResizing, setIsResizing] = useState<'glowpick' | 'skinseoul' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Modal states
  const [showIngredientsModal, setShowIngredientsModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showConfirmReScrape, setShowConfirmReScrape] = useState(false);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Local state for search by ID functionality
  const [searchProductId, setSearchProductId] = useState<string>('');

  // Use navigate
  const navigate = useNavigate();

  // Ref to track if URL parameters have been processed
  const urlParamsProcessed = useRef(false);

  // Helper function for showing toast messages
  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 2000);
  };

  // SKU search handlers
  const handleSkuSearch = async (sku: string) => {
    if (!sku.trim()) {
      return;
    }

    setSkuSearchLoading(true);
    setSkuSearchValue(sku);

    try {
      const searchResults = await skinSeoulProductsClient.searchProductsBySku(sku);
      setSkinSeoulProducts(searchResults);

      if (searchResults.length === 0) {
        showToastMessage(`No products found for SKU: ${sku}`);
      } else {
        showToastMessage(
          `Found ${searchResults.length} product${searchResults.length !== 1 ? 's' : ''} for SKU: ${sku}`,
        );
      }
    } catch (error) {
      console.error('Error searching products by SKU:', error);
      showToastMessage('Error searching for products. Please try again.');
      setSkinSeoulProducts([]);
    } finally {
      setSkuSearchLoading(false);
    }
  };

  const handleClearSkuSearch = () => {
    setSkuSearchValue('');
    setSkinSeoulProducts([]);
    showToastMessage('SKU search cleared');
  };

  // Shared function for searching by product ID (used by both URL navigation and manual search)
  const performProductIdSearch = useCallback(
    async (productId: string, fromUrl = false): Promise<boolean> => {
      try {
        const product = await fetchProductDetails(productId);
        if (product) {
          // Set the main products list to show this single product (like category navigation)
          setSearchProduct(product);

          showToastMessage(`Product found: ${product.brand} ${product.name}`);

          // If this search was initiated manually (not from URL), update the URL
          if (!fromUrl) {
            setTimeout(() => {
              // Mark URL parameters as processed to prevent re-processing when navigating
              urlParamsProcessed.current = true;
              navigate(ROUTES.sourcing.matchingWithGlowpickId(productId));
            }, 100);
          }

          return true;
        } else {
          showToastMessage(`Product with ID ${productId} not found`);
          return false;
        }
      } catch (error) {
        console.error('Error searching for product:', error);
        showToastMessage('Error searching for product');
        return false;
      }
    },
    [fetchProductDetails, setSearchProduct, navigate],
  );

  // Wrapper function for tab changes that also updates URL
  const handleTabChangeWithNavigation = React.useCallback(
    (tab: NavigationTab) => {
      // Update the URL based on the tab
      switch (tab) {
        case 'newest':
        case 'awards':
          navigate(ROUTES.sourcing.matching);
          break;
        case 'search': {
          // For search tab, preserve glowpickId based on multiple sources for consistency
          const currentSearchedProduct = glowpickProducts.length === 1 ? glowpickProducts[0] : null;
          const productIdToUse = glowpickId || currentSearchedProduct?.productId || searchProductId.trim();

          if (productIdToUse) {
            navigate(ROUTES.sourcing.matchingWithGlowpickId(productIdToUse));
          } else {
            navigate(ROUTES.sourcing.matching);
          }
          break;
        }
        case 'brands':
          if (selectedBrand) {
            navigate(ROUTES.sourcing.matchingWithBrand(selectedBrand));
          } else {
            navigate(ROUTES.sourcing.matching);
          }
          break;
        case 'category':
          if (selectedCategory && selectedSubcategory) {
            navigate(ROUTES.sourcing.matchingWithCategory(selectedCategory, selectedSubcategory));
          } else if (selectedCategory) {
            navigate(ROUTES.sourcing.matchingWithCategory(selectedCategory));
          } else {
            navigate(ROUTES.sourcing.matching);
          }
          break;
      }

      // Call the original tab change handler
      handleTabChange(tab);
    },
    [
      navigate,
      selectedBrand,
      selectedCategory,
      selectedSubcategory,
      handleTabChange,
      glowpickId,
      glowpickProducts,
      searchProductId,
    ],
  );

  // Wrapper function for category changes that also updates URL
  const handleCategoryChangeWithNavigation = React.useCallback(
    (category: string) => {
      // Update the URL based on current tab and category
      if (activeTab === 'brands') {
        if (category) {
          navigate(ROUTES.sourcing.matchingWithBrand(category));
        } else {
          navigate(ROUTES.sourcing.matching);
        }
      } else if (activeTab === 'category') {
        if (category) {
          navigate(ROUTES.sourcing.matchingWithCategory(category));
        } else {
          navigate(ROUTES.sourcing.matching);
        }
      }

      // Call the original category change handler
      handleCategoryChange(category);
    },
    [navigate, activeTab, handleCategoryChange],
  );

  // Wrapper function for subcategory changes that also updates URL
  const handleSubcategoryChangeWithNavigation = React.useCallback(
    (subcategory: string) => {
      // Update the URL for category with subcategory
      if (activeTab === 'category' && selectedCategory) {
        if (subcategory) {
          navigate(ROUTES.sourcing.matchingWithCategory(selectedCategory, subcategory));
        } else {
          navigate(ROUTES.sourcing.matchingWithCategory(selectedCategory));
        }
      }

      // Call the original subcategory change handler
      setSelectedSubcategory(subcategory);
    },
    [navigate, activeTab, selectedCategory, setSelectedSubcategory],
  );

  // Handle URL parameters on component mount
  React.useEffect(() => {
    // Only process URL parameters once
    if (urlParamsProcessed.current) {
      return;
    }

    // Add a delay to ensure the component is fully initialized
    const timeoutId = setTimeout(async () => {
      if (glowpickId) {
        // Set the product ID in the search input
        setSearchProductId(glowpickId);

        // Perform the search first to get the product
        const searchSuccess = await performProductIdSearch(glowpickId, true);

        // Only switch to search tab after we have the product data
        if (searchSuccess) {
          handleTabChange('search');
        }

        urlParamsProcessed.current = true;
      } else if (brand) {
        // Switch to brands tab and set brand filter
        const decodedBrand = decodeURIComponent(brand);

        // First, switch to brands tab without navigation to prevent URL conflicts
        handleTabChange('brands');

        // Then set the brand filter after a delay to ensure tab is set
        setTimeout(() => {
          // Directly set the brand instead of relying on handleCategoryChange timing
          setSelectedBrand(decodedBrand);

          // Finally update the URL to match the current state
          setTimeout(() => {
            navigate(ROUTES.sourcing.matchingWithBrand(decodedBrand));
          }, 100);
        }, 1500); // Increased timeout to ensure brands data is loaded
        urlParamsProcessed.current = true;
      } else if (category) {
        // Switch to category tab and set category/subcategory filters
        const decodedCategory = decodeURIComponent(category);
        const decodedSubcategory = subcategory ? decodeURIComponent(subcategory) : '';

        // First switch to category tab without navigation to prevent premature API calls
        handleTabChange('category');

        // Wait for tab change and categories data to load, then set both category and subcategory together
        setTimeout(() => {
          // Set category first
          handleCategoryChange(decodedCategory);

          // If subcategory exists, set it immediately after category (without delay)
          if (decodedSubcategory) {
            // Small timeout to ensure category is set
            setTimeout(() => {
              setSelectedSubcategory(decodedSubcategory);
              // Update URL to match the current state
              navigate(ROUTES.sourcing.matchingWithCategory(decodedCategory, decodedSubcategory));
            }, 50);
          } else {
            // Update URL for category only
            navigate(ROUTES.sourcing.matchingWithCategory(decodedCategory));
          }
        }, 1500); // Wait for categories data to load
        urlParamsProcessed.current = true;
      }
    }, 100); // Small delay to ensure component is initialized

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Separate effect to watch for URL param changes after initial mount
  React.useEffect(() => {
    // Don't run this on initial mount
    if (!urlParamsProcessed.current) {
      return;
    }

    // Reset the flag when URL parameters actually change
    urlParamsProcessed.current = false;
  }, [glowpickId, brand, category, subcategory]);

  // Handle search by product ID
  const handleSearchById = async () => {
    if (!searchProductId.trim()) {
      showToastMessage('Please enter a product ID');
      return;
    }

    // Use the shared search function for manual search
    await performProductIdSearch(searchProductId.trim(), false);
  };

  // Resizable splitter handlers
  const handleMouseDown = (splitter: 'glowpick' | 'skinseoul') => (e: React.MouseEvent) => {
    setIsResizing(splitter);
    e.preventDefault();
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const containerWidth = containerRect.width;
      const mousePercentage = (mouseX / containerWidth) * 100;

      if (isResizing === 'glowpick') {
        // Resizing between Glowpick and SkinSeoul
        const newGlowpickWidth = Math.min(Math.max(mousePercentage, 20), 60); // 20% to 60%
        setGlowpickWidth(newGlowpickWidth);
      } else if (isResizing === 'skinseoul') {
        // Resizing between SkinSeoul and Coupang
        const newSkinSeoulWidth = Math.min(Math.max(mousePercentage - glowpickWidth, 20), 60); // 20% to 60%
        setSkinSeoulWidth(newSkinSeoulWidth);
      }
    },
    [isResizing, glowpickWidth],
  );

  const handleMouseUp = React.useCallback(() => {
    setIsResizing(null);
  }, []);

  // Add event listeners for mouse move and up
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleGlowpickProductClick = async (product: GlowpickProduct) => {
    setSelectedGlowpickProduct(product);
    // Clear previous Coupang selection when switching Glowpick products
    setSelectedCoupangProduct(null);

    // Clear SKU search when selecting a Glowpick product
    setSkuSearchValue('');

    // Fetch full product details if needed
    const fullProduct = getProductById(product.productId);
    if (fullProduct) {
      setSelectedGlowpickProduct(fullProduct);
    }

    // Query SkinSeoul products by GPI
    setSkinSeoulLoading(true);
    try {
      const response = await skinSeoulProductsClient.getAllProducts(
        1,
        100,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        product.productId,
      );
      setSkinSeoulProducts(response.products);
    } catch (error) {
      console.error('Failed to fetch SkinSeoul products by GPI:', error);
      setSkinSeoulProducts([]);
    } finally {
      setSkinSeoulLoading(false);
    }

    // Use the Coupang hook to search for products
    await searchCoupangProducts(product);
  };

  const handleShowIngredients = async (product: GlowpickProduct) => {
    try {
      // Fetch fresh product details from API server
      const fullProduct = await fetchProductDetails(product.productId);
      if (fullProduct) {
        setSelectedGlowpickProduct(fullProduct);
        setShowIngredientsModal(true);
      } else {
        // Fallback to local product if API call fails
        setSelectedGlowpickProduct(product);
        setShowIngredientsModal(true);
      }
    } catch (error) {
      console.error('Error fetching product details for ingredients:', error);
      // Fallback to local product
      setSelectedGlowpickProduct(product);
      setShowIngredientsModal(true);
    }
  };

  const handleShowDescription = async (product: GlowpickProduct) => {
    try {
      // Fetch fresh product details from API server
      const fullProduct = await fetchProductDetails(product.productId);
      if (fullProduct) {
        setSelectedGlowpickProduct(fullProduct);
        setShowDescriptionModal(true);
      } else {
        // Fallback to local product if API call fails
        setSelectedGlowpickProduct(product);
        setShowDescriptionModal(true);
      }
    } catch (error) {
      console.error('Error fetching product details for description:', error);
      // Fallback to local product
      setSelectedGlowpickProduct(product);
      setShowDescriptionModal(true);
    }
  };

  // Handle metadata updates from child components
  const handleMetadataUpdate = (productId: string, metadata: Record<string, any>) => {
    // Update selected product if it matches
    if (selectedGlowpickProduct && selectedGlowpickProduct.productId === productId) {
      setSelectedGlowpickProduct({
        ...selectedGlowpickProduct,
        metadata: {
          ...selectedGlowpickProduct.metadata,
          ...metadata,
        },
      });
    }
  };

  // Handle successful matches to refresh Glowpick list
  const handleMatchSuccess = async () => {
    console.info('🔄 Match success - triggering silent refresh of Glowpick products');

    // Silent refresh the main Glowpick products list to update match counts
    silentRefresh();

    // Note: We don't update selectedGlowpickProduct here because:
    // 1. Metadata is already updated locally via onMetadataUpdate for immediate UI feedback
    // 2. Updating selectedGlowpickProduct here would cause focus to jump when user has switched products
    // 3. The silentRefresh() already updates the product list with latest match counts
  };

  // Delete existing matches for a product
  const deleteExistingMatches = async (productId: string) => {
    try {
      console.info('🗑️ Deleting existing matches for product:', productId);
      const result = await cpGpMatchClient.getMatchesForGpProduct(productId);

      if (result.matches && result.matches.length > 0) {
        console.info(`Found ${result.matches.length} existing matches to delete`);

        // Delete each match
        for (const match of result.matches) {
          const deleteResult = await cpGpMatchClient.deleteMatch(match.cpProductId, match.cpItemId);
          if (!deleteResult.success) {
            console.error(`❌ Failed to delete match ${match.cpProductId}-${match.cpItemId}:`, deleteResult.message);
          }
        }
        console.info('✅ All existing matches deleted');
      } else {
        console.info('No existing matches found to delete');
      }
    } catch (error) {
      console.error('❌ Error deleting existing matches:', error);
      throw error;
    }
  };

  // Show confirmation dialog for re-scraping
  const handleReScrapeClick = () => {
    setShowConfirmReScrape(true);
  };

  // Handle confirmed re-scraping
  const handleConfirmedReScrape = async () => {
    setShowConfirmReScrape(false);

    if (selectedGlowpickProduct) {
      console.info('🔄 Re-scraping Coupang search for:', selectedGlowpickProduct.brand, selectedGlowpickProduct.name);
      showToastMessage('Starting complete re-scrape process...');

      try {
        // Step 1: Delete existing matches
        await deleteExistingMatches(selectedGlowpickProduct.productId);
        showToastMessage('Deleted existing matches, clearing metadata...');

        // Step 2: Clear existing metadata
        const clearResult = await clearProductMetadata(selectedGlowpickProduct.productId);
        if (!clearResult.success) {
          showToastMessage('Failed to clear product metadata');
          console.error('❌ Failed to clear metadata:', clearResult.message);
          return;
        }

        // Step 3: Delete keyword search results
        showToastMessage('Deleting keyword search results...');
        const deleteResult = await deleteKeywordSearchResults(selectedGlowpickProduct.productId);
        if (!deleteResult.success) {
          console.warn('⚠️ Failed to delete keyword search results:', deleteResult.message);
          // Continue with batch job even if deletion fails
        }

        // Step 4: Trigger the batch job
        showToastMessage('Triggering re-scrape batch job...');
        const batchResult = await triggerScrapeBatchJob(selectedGlowpickProduct.productId);
        if (batchResult.success) {
          showToastMessage(`Re-scrape batch job started (Job ID: ${batchResult.jobId})`);
          console.info('✅ Batch job triggered successfully:', batchResult.jobId);

          // Step 5: Clear the Coupang search results list
          clearCoupangProducts();

          showToastMessage('Re-scrape job started successfully');
        } else {
          showToastMessage('Failed to trigger re-scrape batch job');
          console.error('❌ Failed to trigger batch job:', batchResult.message);
        }
      } catch (error) {
        showToastMessage('Error during re-scrape process');
        console.error('❌ Error during re-scrape process:', error);
      }
    }
  };

  // Handle re-scraping (for backward compatibility)
  const handleReScrape = handleReScrapeClick;

  // Handle initial scraping (no confirmation needed)
  const handleScrape = async () => {
    if (selectedGlowpickProduct) {
      console.info('🔄 Initial scraping for:', selectedGlowpickProduct.brand, selectedGlowpickProduct.name);
      showToastMessage('Triggering scrape batch job...');

      try {
        // For initial scrape, we might still want to clear any existing metadata just to be safe
        const clearResult = await clearProductMetadata(selectedGlowpickProduct.productId);
        if (!clearResult.success) {
          console.warn('⚠️ Failed to clear metadata, but continuing with batch job:', clearResult.message);
        }

        // Trigger the batch job
        const batchResult = await triggerScrapeBatchJob(selectedGlowpickProduct.productId);
        if (batchResult.success) {
          showToastMessage(`Scrape batch job started (Job ID: ${batchResult.jobId})`);
          console.info('✅ Batch job triggered successfully:', batchResult.jobId);

          // Clear the Coupang search results list
          clearCoupangProducts();

          showToastMessage('Scrape job started successfully');
        } else {
          showToastMessage('Failed to trigger scrape batch job');
          console.error('❌ Failed to trigger batch job:', batchResult.message);
        }
      } catch (error) {
        showToastMessage('Error during scrape process');
        console.error('❌ Error during scrape process:', error);
      }
    }
  };

  // Handle "No match found" button click
  const handleNoMatchFound = async () => {
    if (selectedGlowpickProduct) {
      console.info(
        '🚫 Marking product as not found in Coupang:',
        selectedGlowpickProduct.brand,
        selectedGlowpickProduct.name,
      );
      showToastMessage('Marking product as not found in Coupang...');

      try {
        // Import glowpickClient for metadata update
        const { glowpickClient } = await import('../../../infrastructure/GlowpickClient');

        // Update metadata with notFoundInCoupang: true
        const updateResult = await glowpickClient.updateProductMetadata(selectedGlowpickProduct.productId, {
          notFoundInCoupang: true,
        });

        if (updateResult.success) {
          showToastMessage('Product marked as not found in Coupang');
          console.info('✅ Product metadata updated with notFoundInCoupang: true');

          // Update the local state to reflect the change
          handleMetadataUpdate(selectedGlowpickProduct.productId, {
            notFoundInCoupang: true,
          });

          // Trigger silent refresh to update the main product list
          silentRefresh();
        } else {
          showToastMessage('Failed to mark product as not found');
          console.error('❌ Failed to update product metadata:', updateResult.message);
        }
      } catch (error) {
        showToastMessage('Error marking product as not found');
        console.error('❌ Error updating product metadata:', error);
      }
    }
  };

  return (
    <Container fluid className="py-1" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="mb-2">
        <span className="h5 mb-1">GP-CP Matching</span>
      </div>

      {/* Resizable Layout Container */}
      <div
        ref={containerRef}
        className="flex-grow-1"
        style={{
          height: 'calc(100% - 100px)',
          display: 'flex',
          position: 'relative',
        }}
      >
        {/* Glowpick Products Panel */}
        <div style={{ width: `${glowpickWidth}%`, minWidth: '300px' }}>
          <Card className="h-100">
            <Card.Body className="p-0">
              {/* Glowpick Products List */}
              <div ref={scrollContainerRef} style={{ height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
                <GlowpickProductsTable
                  loading={loading}
                  glowpickProducts={glowpickProducts}
                  selectedGlowpickProduct={selectedGlowpickProduct}
                  onProductClick={handleGlowpickProductClick}
                  onShowIngredients={handleShowIngredients}
                  onShowDescription={handleShowDescription}
                  activeTab={activeTab}
                  brands={brands}
                  categories={categories}
                  subcategories={subcategories}
                  awardCategories={awardCategories}
                  selectedCategory={selectedCategory}
                  selectedSubcategory={selectedSubcategory}
                  selectedBrand={selectedBrand}
                  selectedRankingType={selectedRankingType}
                  selectedAwardPeriod={selectedAwardPeriod}
                  sortBy={sortBy}
                  onTabChange={handleTabChangeWithNavigation}
                  onCategoryChange={handleCategoryChangeWithNavigation}
                  onSubcategoryChange={handleSubcategoryChangeWithNavigation}
                  onRankingTypeChange={setSelectedRankingType}
                  onAwardPeriodChange={setSelectedAwardPeriod}
                  onSortChange={setSortBy}
                  lastUpdatedInfo={lastUpdatedInfo}
                  queryLimit={queryLimit}
                  onQueryLimitChange={setQueryLimit}
                  paginationInfo={paginationInfo}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  searchProductId={searchProductId}
                  onSearchProductIdChange={setSearchProductId}
                  onSearchById={handleSearchById}
                />
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* First Resizable Splitter (Glowpick | SkinSeoul) */}
        <div
          onMouseDown={handleMouseDown('glowpick')}
          style={{
            width: '8px',
            backgroundColor: '#dee2e6',
            cursor: 'col-resize',
            position: 'relative',
            flexShrink: 0,
            borderLeft: '1px solid #ccc',
            borderRight: '1px solid #ccc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Visual indicator for the splitter */}
          <div
            style={{
              width: '2px',
              height: '40px',
              backgroundColor: '#6c757d',
              borderRadius: '1px',
            }}
          />
        </div>

        {/* SkinSeoul Products Panel */}
        <div style={{ width: `${skinSeoulWidth}%`, minWidth: '300px' }}>
          <SkinSeoulProductsTable
            products={skinSeoulProducts}
            loading={skinSeoulLoading}
            skuSearchValue={skuSearchValue}
            onSkuSearch={handleSkuSearch}
            onClearSkuSearch={handleClearSkuSearch}
            skuSearchLoading={skuSearchLoading}
          />
        </div>

        {/* Second Resizable Splitter (SkinSeoul | Coupang) */}
        <div
          onMouseDown={handleMouseDown('skinseoul')}
          style={{
            width: '8px',
            backgroundColor: '#dee2e6',
            cursor: 'col-resize',
            position: 'relative',
            flexShrink: 0,
            borderLeft: '1px solid #ccc',
            borderRight: '1px solid #ccc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Visual indicator for the splitter */}
          <div
            style={{
              width: '2px',
              height: '40px',
              backgroundColor: '#6c757d',
              borderRadius: '1px',
            }}
          />
        </div>

        {/* Coupang Products Panel */}
        <div style={{ width: `${100 - glowpickWidth - skinSeoulWidth}%`, minWidth: '300px' }}>
          <Card className="h-100">
            <Card.Header
              className="text-success"
              style={{
                padding: '12px 12px',
                minHeight: '66px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="d-flex align-items-center">
                  <img
                    src="https://logo.clearbit.com/coupang.com"
                    alt="Coupang"
                    style={{
                      height: '24px',
                      margin: '0 8px 0 0',
                    }}
                    onError={(e) => {
                      // Fallback to text if image fails to load
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = 'none';
                      const nextElement = target.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'inline';
                      }
                    }}
                  />
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: '1.5rem',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      letterSpacing: '-0.5px',
                      display: 'none',
                    }}
                  >
                    Coupang
                  </span>
                </div>

                {selectedGlowpickProduct && (
                  <div className="flex-grow-1 ms-3">
                    <small>
                      {!coupangLoading && (
                        <span>
                          {coupangProducts.length} result{coupangProducts.length !== 1 ? 's' : ''} for{' '}
                        </span>
                      )}
                      {coupangLoading && <span>Searching for </span>}
                      <a
                        href={`https://www.coupang.com/np/search?component=&q=${encodeURIComponent(
                          `${selectedGlowpickProduct.brand} ${selectedGlowpickProduct.name}`,
                        )}&channel=user`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedGlowpickProduct.brand} {selectedGlowpickProduct.name}
                      </a>
                    </small>
                  </div>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-0 d-flex flex-column h-100">
              {/* Fixed Header with actions */}
              {selectedGlowpickProduct && (
                <div className="d-flex justify-content-between align-items-center p-1 border-bottom flex-shrink-0">
                  <div className="d-flex align-items-center gap-3">
                    <IdBadge type={IdBadgeType.GPI} value={selectedGlowpickProduct.productId} size="sm" />
                    {coupangProducts.length > 0 &&
                      (() => {
                        const latestDate = coupangProducts
                          .map((product) => product.updatedAt)
                          .filter((date) => date)
                          .sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime())[0];
                        return latestDate ? (
                          <small className="text-primary">Last scraped: {convertToLocalDate(latestDate)}</small>
                        ) : null;
                      })()}
                  </div>
                  <div className="d-flex gap-2">
                    {/* Scrape/Re-scrape button */}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => (coupangProducts.length === 0 ? handleScrape() : handleReScrape())}
                      disabled={coupangLoading}
                      title={
                        coupangProducts.length === 0
                          ? 'Scrape Coupang search results'
                          : 'Re-scrape Coupang search results'
                      }
                    >
                      <RefreshCw size={16} className="me-2" />
                      {coupangProducts.length === 0 ? 'Scrape' : 'Re-scrape'}
                    </Button>

                    <Button variant="outline-warning" size="sm" onClick={handleNoMatchFound}>
                      <X size={16} className="me-2" />
                      No match found
                    </Button>
                  </div>
                </div>
              )}

              {/* Scrollable Coupang Products List */}
              <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
                <CoupangProductsTable
                  selectedGlowpickProduct={selectedGlowpickProduct}
                  cpSearchList={coupangProducts}
                  loading={coupangLoading}
                  onShowToast={showToastMessage}
                  onMetadataUpdate={handleMetadataUpdate}
                  onMatchSuccess={handleMatchSuccess}
                />
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Use the correct modal components */}
      <IngredientsModal
        show={showIngredientsModal}
        onHide={() => setShowIngredientsModal(false)}
        product={selectedGlowpickProduct}
      />

      <DescriptionModal
        show={showDescriptionModal}
        onHide={() => setShowDescriptionModal(false)}
        product={selectedGlowpickProduct}
      />

      {/* Confirmation Modal for Re-scraping */}
      <Modal show={showConfirmReScrape} onHide={() => setShowConfirmReScrape(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Re-scrape</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>This will completely reset the search for:</strong>
          </p>
          <p className="text-primary fw-bold">
            {selectedGlowpickProduct?.brand} {selectedGlowpickProduct?.name}
          </p>
          <p>
            <strong>The following actions will be performed:</strong>
          </p>
          <ul>
            <li>Delete all existing matches</li>
            <li>Clear product metadata</li>
            <li>Trigger new Coupang scraping batch job</li>
          </ul>
          <p className="text-warning">
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmReScrape(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmedReScrape}>
            Confirm Re-scrape
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Use the new Toast component */}
      <Toast message={toastMessage} isVisible={showToast} />
    </Container>
  );
};

export default SourcingPage;
